import { useEquipmentCatalog } from '@/util/useEquipmentCatalog';
import { decodeCatalogModel, type ModelDecode } from '@rock/models';
import type { Equipment } from '@rock/types';
import { useMemo } from 'react';

/**
 * Read what this equipment's MODEL NUMBER says it is, from the manufacturer's own naming scheme.
 * Sibling of `useSerialDecode` — same catalog pair, the other half of the nameplate.
 *
 * Deliberately decodes the matched model's `modelCanonical`, NOT the equipment's own
 * `fields.modelNumber`: the canonical value is the reconciled one, so this can never disagree with the
 * model page about the same appliance. Equipment that matched no model therefore decodes nothing, which
 * also keeps the brand gate on the one path `decodeCatalogModel` vets (see its `catalogBrandNames`).
 *
 * Undefined whenever the brand has no scheme or the number resolves to more than one product — callers
 * render nothing, never an error or an empty state.
 */
export function useModelDecode(equipment: Equipment): ModelDecode | undefined {
    const { model, brand } = useEquipmentCatalog(equipment);
    return useMemo(() => (model ? decodeCatalogModel(model, brand) : undefined), [model, brand]);
}
